// Commander Watchtower - the client's whole contract with the feature.
//
// Reads go straight to Supabase under RLS. There is no API read path and none
// is needed: the `watchtower_reports` policy already scopes a SELECT to the
// signed-in user AND the active run of the report's family, so "which report
// may this person see" is a server-derived fact, not something this module
// filters for. Everything here does is order, join the read receipt, and call
// the three RPCs.
//
// EVERY user-facing string lives in `report_json`. This module (and the page)
// compute no copy: the generator owns the words, the client owns the layout.
// Vocabulary in any string we do author: upcoming / unreleased, never
// "spoiled".

import { supabase } from './supabase'

/**
 * Every `schema_version` this client can render.
 *
 * Two frozen artifacts, not one evolving document: v1
 * (watchtower-report-schema.json) is closed and forbids extra properties, so
 * the fit tier got its own file (watchtower-report-schema.v2.json). A report
 * declaring anything else is `unsupported` - see `parseReport`.
 */
export const WATCHTOWER_SCHEMA_VERSIONS = [1, 2] as const

export type WatchtowerSchemaVersion = (typeof WATCHTOWER_SCHEMA_VERSIONS)[number]

/** The newest version this client renders. */
export const WATCHTOWER_SCHEMA_VERSION: WatchtowerSchemaVersion = 2

/**
 * The one sentence this module authors.
 *
 * A body whose version we cannot decode is the single case where no string is
 * available from the report itself, so the client has to say something. The
 * page and the archive both import THIS constant so the wording cannot drift
 * between them.
 */
export const WATCHTOWER_UNSUPPORTED_TEXT = 'This report needs an app update.'

// --- report_json, transcribed BY HAND from
// docs/superpowers/specs/watchtower-report-schema.json (schema_version 1) and
// watchtower-report-schema.v2.json (schema_version 2). Field names are
// identical to the schemas on purpose: a rename here would be a silent
// contract break that no test could see.
//
// v2 = v1 plus `picks[].source`, `picks[].fit`, a top-level `fit_tier`, and a
// `receipt.synergy` that may be null on a fit pick. All of those are OPTIONAL
// on the type, because a v1 body carries none of them - not even as a null -
// and the v1 fixtures have to keep type-checking untouched.

export type WatchtowerRunLabel = 'Early look' | 'Release-day refresh' | 'Set check'
export type WatchtowerPhase = 'prerelease' | 'release_day' | 'set_check'

export interface WatchtowerHeader {
  family_id: string
  family_label: string
  run_label: WatchtowerRunLabel
  phase: WatchtowerPhase
  /** Present only on an on-demand set check (API 2026-09-24); batch reports never carry it. */
  kind?: 'set_check'
  deck_id: string
  deck_name: string
  /** ordered command-zone oracle ids */
  commander_key: string[]
  commander_names: string[]
  /** Scryfall card id for the commander art; null when unresolved */
  art_card_id: string | null
  /** the EDHREC page timestamp the picks came from; null only with a page_missing zero state */
  page_ts: string | null
  generated_at: string
}

export interface WatchtowerVerdict {
  count: number
  text: string
}

export interface WatchtowerReceipt {
  num_decks: number
  potential_decks: number
  inclusion: number
  /** edhrec picks: strictly positive. fit picks: null when the attributed page carries no synergy for the card */
  synergy: number | null
  section: string
  page_ts: string
  /** partner decks: which command-zone page this row came from */
  source_commander: string | null
  /** the dedicated receipt formatter's output, rendered verbatim */
  text: string
}

export interface WatchtowerReplaces {
  name: string
  /** the swap scorer's reason */
  reason: string
}

export type WatchtowerImpactLevel = 'show' | 'estimate' | 'suppress'

export interface WatchtowerImpact {
  level: WatchtowerImpactLevel
  win_turn_delta: number | null
  bracket_from: number
  bracket_to: number | null
  /** null when suppressed */
  text: string | null
}

/**
 * Which tier produced a pick (v2 only).
 *
 * `edhrec` crossed the support rule on the commander's page; `fit` came from
 * the deterministic fit tier, which only runs on a report with zero edhrec
 * picks. The two are never visually identical, which is what the source chip
 * is for.
 */
export type WatchtowerPickSource = 'edhrec' | 'fit'

export interface WatchtowerFit {
  /** the deck's engine label as rendered */
  engine: string
  /** the IPOM role, null when the analysis said utility */
  role: string | null
  fit_score: number
  /** the winning sub-signal's closed detail phrase */
  signal: string
  /** the one-line reason, server-authored; rendered verbatim */
  text: string
}

export interface WatchtowerFitTier {
  enabled: boolean
  /** false (both counts 0) when the tier was skipped because an edhrec pick existed */
  ran: boolean
  candidates: number
  admitted: number
}

export interface WatchtowerPick {
  oracle_id: string
  name: string
  mana_cost: string | null
  image_uri: string | null
  /** null renders as "Price unavailable"; never zero */
  price_usd: number | null
  receipt: WatchtowerReceipt
  /** null when the package operation found no safe cut for this pick */
  replaces: WatchtowerReplaces | null
  impact: WatchtowerImpact
  /** v2 only; absent on a v1 body */
  source?: WatchtowerPickSource
  /** v2 only; non-null exactly when `source` is `fit` */
  fit?: WatchtowerFit | null
}

export type WatchtowerPackageStatus = 'validated' | 'partial' | 'none'

export interface WatchtowerPackage {
  status: WatchtowerPackageStatus
  /** only when every applied pick has a price; otherwise the client shows the labelled subtotal */
  price_total: number | null
  win_turn_before: number
  win_turn_after: number | null
  bracket_before: number
  bracket_after: number | null
  /** the whole-package line; null means render the subtotal rule instead */
  text: string | null
}

export interface WatchtowerExplorationCard {
  oracle_id: string
  name: string
  mana_cost: string | null
  image_uri: string | null
  price_usd: number | null
  type_line: string
}

export interface WatchtowerExploration {
  heading: string
  cards: WatchtowerExplorationCard[]
}

export type WatchtowerZeroStateReason =
  | 'below_support'
  | 'synergy_missing'
  | 'nonpositive_synergy'
  | 'already_in_deck'
  | 'no_safe_cut'
  | 'page_stale'
  | 'page_missing'
  | 'unresolved_names'
  | 'command_zone_unresolved'

export interface WatchtowerZeroState {
  reason: WatchtowerZeroStateReason
  /** the numbers the reason rests on, e.g. {"best_num_decks": 7, "min_num_decks": 10} */
  values: Record<string, unknown>
  page_ts: string | null
  text: string
}

export interface WatchtowerReport {
  schema_version: WatchtowerSchemaVersion
  header: WatchtowerHeader
  verdict: WatchtowerVerdict
  picks: WatchtowerPick[]
  package: WatchtowerPackage
  exploration: WatchtowerExploration | null
  zero_state: WatchtowerZeroState | null
  /** true only on the free campaign run; renders the upgrade band */
  cta: boolean
  /** v2 only; required there, absent on a v1 body */
  fit_tier?: WatchtowerFitTier
}

// --- decode
//
// Mixed archives are the normal state: an archive holds v1 reports (every run
// before the fit tier, and every non-fit run after it) next to v2 reports. So
// the version is DISPATCHED ON, never assumed, and a version this build does
// not know is a rendering state rather than an exception. Dropping such a
// report would hide something the server published for this person; throwing
// would take the surrounding surface down with it.

/**
 * `{ kind: 'ok' }` for a v1 or v2 body this build can render, `unsupported`
 * for anything else, carrying whatever the body declared so a test (or a
 * future log line) can see what it was. NEVER throws.
 */
export type ParsedReport =
  | { kind: 'ok'; report: WatchtowerReport }
  | { kind: 'unsupported'; schemaVersion: unknown }

function isObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

/**
 * Decode a `report_json` body.
 *
 * Two gates, and deliberately only two:
 *
 *   1. `schema_version` is one this build renders. That is the contract
 *      question the spec makes both clients answer.
 *   2. the few members every renderer dereferences UNCONDITIONALLY are the
 *      right kind of thing (`header` with a `commander_names` array, plus
 *      `verdict`, `picks`, `package`). A body that fails this is not one the
 *      generator can emit, and rendering it would throw.
 *
 * It does NOT re-validate the schemas field by field. They are frozen
 * artifacts, checked against the fixtures by watchtowerSchema.test.ts, and a
 * second hand-written copy of them here would be one more thing to keep in
 * step. `WatchtowerReportRow.report_json` stays typed as a report for the same
 * reason the row's other columns are: it is the shape the server publishes.
 * This function is the gate in front of it, not a replacement for it.
 */
export function parseReport(json: unknown): ParsedReport {
  const version = isObject(json) ? json.schema_version : undefined
  const known = WATCHTOWER_SCHEMA_VERSIONS.some(v => v === version)
  if (!known) return { kind: 'unsupported', schemaVersion: version }
  const body = json as Record<string, unknown>
  const header = body.header
  const structural =
    isObject(header) &&
    Array.isArray(header.commander_names) &&
    isObject(body.verdict) &&
    Array.isArray(body.picks) &&
    isObject(body.package)
  if (!structural) return { kind: 'unsupported', schemaVersion: version }
  return { kind: 'ok', report: body as unknown as WatchtowerReport }
}

/**
 * The deck name out of a body we may not be able to decode, so the list and
 * archive rows an unsupported report still gets can name it. Null when it is
 * not a readable string, in which case the caller labels the row itself.
 */
export function readDeckName(json: unknown): string | null {
  const header = isObject(json) ? json.header : undefined
  const name = isObject(header) ? header.deck_name : undefined
  if (typeof name !== 'string') return null
  const trimmed = name.trim()
  return trimmed.length > 0 ? trimmed : null
}

/**
 * The set family's display label (`header.family_label`, the line the report
 * page prints as its eyebrow) out of a body, or null when it is missing or
 * blank. Same shape as `readDeckName`: the caller decides what to do without
 * one. The launch popup drops its "<label> is coming." sentence rather than
 * print the family ID, which is an internal key and not a sentence.
 */
export function readFamilyLabel(json: unknown): string | null {
  const header = isObject(json) ? json.header : undefined
  const label = isObject(header) ? header.family_label : undefined
  if (typeof label !== 'string') return null
  const trimmed = label.trim()
  return trimmed.length > 0 ? trimmed : null
}

// --- rows

/** A `watchtower_reports` row plus the client-side join of its read receipt. */
export interface WatchtowerReportRow {
  id: string
  run_id: string
  input_id: string
  user_id: string
  deck_id: string
  commander_key: string[] | null
  source_ts: string | null
  source_hash: string | null
  report_json: WatchtowerReport
  superseded_at: string | null
  created_at: string
  /** from watchtower_report_delivery; null means never opened */
  seen_at: string | null
}

const REPORT_COLUMNS =
  'id, run_id, input_id, user_id, deck_id, commander_key, source_ts, source_hash, report_json, superseded_at, created_at'

// --- errors
//
// Two error shapes matter to callers and nothing else does:
//
//   deck_busy      a saved-deck write lost a 3s lock race with a publish
//                  (amendment 2). The SQLSTATE travels in the PostgREST error
//                  BODY, never the HTTP status, so we key on `code`.
//   unavailable    the RPC does not exist in the schema cache yet. During the
//                  build-out that is the normal state, and it is never an
//                  error a user should read.

/**
 * True when a Supabase/PostgREST error is Postgres `55P03` lock_not_available.
 *
 * Amendment 2: clients write `saved_decks` through PostgREST directly, so the
 * publish transaction protects itself with a `lock_timeout` pre-request hook
 * rather than an API-side setting. A write that loses that race is RETRYABLE:
 * keep the edit in editor state, retry, never show a generic error. The HTTP
 * status for this is a plain 500, which is why nothing may key on it.
 *
 * Exported (with tests) ahead of the saved-deck wiring, which is Task D5.
 */
export function deckBusy(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const direct = (error as { code?: unknown }).code
  if (direct === '55P03') return true
  // Some transports hand back { error: { code } } rather than the body itself.
  const nested = (error as { error?: { code?: unknown } }).error
  return !!nested && typeof nested === 'object' && nested.code === '55P03'
}

/**
 * True when a PostgREST error means "this function is not in the schema cache",
 * which is what a not-yet-deployed RPC looks like from here. PostgREST answers
 * 404 with `PGRST202`; older builds only carry the message.
 */
function missingFunction(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const e = error as { code?: unknown; message?: unknown; status?: unknown }
  if (e.code === 'PGRST202' || e.code === '42883') return true
  if (e.status === 404) return true
  return typeof e.message === 'string' && /could not find the function/i.test(e.message)
}

// --- reads

/**
 * Every report the signed-in user may currently see, ordered for display
 * (unseen first, then newest, then id).
 *
 * `userId` is passed explicitly so a stale session can never render another
 * user's page, but it is belt-and-braces: RLS already scopes the read to the
 * caller AND to the active run of each report's family, so a superseded run
 * disappears with no client-side filter and no `superseded_at` write.
 */
export async function fetchReports(userId: string): Promise<WatchtowerReportRow[]> {
  if (!supabase || !userId) return []
  const { data, error } = await supabase
    .from('watchtower_reports')
    .select(REPORT_COLUMNS)
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
  if (error || !data) return []
  const rows = (data as unknown as Omit<WatchtowerReportRow, 'seen_at'>[]).map(r => ({
    ...r,
    seen_at: null as string | null,
  }))
  return sortReports(await joinDelivery(rows))
}

/** One report by id, with its read receipt. Null when it is not visible. */
export async function fetchReport(reportId: string, userId: string): Promise<WatchtowerReportRow | null> {
  // Belt and braces like fetchReports: RLS scopes the row to its owner, and
  // the client filters on user_id as well so a misconfigured policy can never
  // turn a guessed report id into another user's report.
  if (!supabase || !reportId || !userId) return null
  const { data, error } = await supabase
    .from('watchtower_reports')
    .select(REPORT_COLUMNS)
    .eq('id', reportId)
    .eq('user_id', userId)
    .maybeSingle()
  if (error || !data) return null
  const row = { ...(data as unknown as Omit<WatchtowerReportRow, 'seen_at'>), seen_at: null as string | null }
  const joined = await joinDelivery([row])
  return joined[0] ?? row
}

/**
 * Client-side join of `watchtower_report_delivery.seen_at`. Deliberately a
 * second round trip rather than a PostgREST embed: the delivery table has its
 * own owner policy and no FK-named relationship the embed syntax could rely on
 * staying stable, and a failed receipt read must degrade to "unseen", never to
 * an empty report list.
 */
async function joinDelivery(rows: WatchtowerReportRow[]): Promise<WatchtowerReportRow[]> {
  if (!supabase || rows.length === 0) return rows
  const { data, error } = await supabase
    .from('watchtower_report_delivery')
    .select('report_id, seen_at')
    .in('report_id', rows.map(r => r.id))
  if (error || !data) return rows
  const seen = new Map<string, string | null>()
  for (const d of data as { report_id: string; seen_at: string | null }[]) {
    seen.set(d.report_id, d.seen_at)
  }
  return rows.map(r => ({ ...r, seen_at: seen.get(r.id) ?? null }))
}

/**
 * Display order: unseen first, then newest, then id.
 *
 * The same ordering the claim RPC uses to resolve "the caller's current visible
 * report", so a deep link and the list agree about which one is on top.
 */
export function sortReports(rows: WatchtowerReportRow[]): WatchtowerReportRow[] {
  return [...rows].sort((a, b) => {
    const aSeen = a.seen_at ? 1 : 0
    const bSeen = b.seen_at ? 1 : 0
    if (aSeen !== bSeen) return aSeen - bSeen
    if (a.created_at !== b.created_at) return a.created_at < b.created_at ? 1 : -1
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
  })
}

// --- claim token
//
// The launch popup is claim-gated so it fires exactly once per person. The
// token is the credential: minted here, hashed server-side on first claim, and
// NEVER overwritten by a later claim, so a second device presenting a different
// token gets nothing instead of stealing the claim.

const CLAIM_TOKEN_PREFIX = 'commander-bracket-watchtower-claim'

export function claimTokenKey(campaign: string, userId: string): string {
  return `${CLAIM_TOKEN_PREFIX}:${campaign}:${userId}`
}

/**
 * The persisted claim token for this (campaign, user), minted once.
 *
 * Persistence is the whole point: a retry after supersession has to present the
 * SAME token or the server returns nothing and leaves the stored hash alone.
 * If storage is unavailable (private mode) we still return a usable uuid, and
 * the caller simply gets one claim attempt for this page view.
 */
export function claimToken(campaign: string, userId: string): string {
  const key = claimTokenKey(campaign, userId)
  try {
    const existing = localStorage.getItem(key)
    if (existing) return existing
  } catch {
    // storage unavailable - mint an ephemeral token below
  }
  const fresh = crypto.randomUUID()
  try {
    localStorage.setItem(key, fresh)
  } catch {
    // ignore; the ephemeral token is still valid for this attempt
  }
  return fresh
}

/**
 * The persisted token WITHOUT minting one.
 *
 * The difference matters exactly once: when the server says this person has
 * already claimed and this device holds no token, a freshly minted one can only
 * ever be rejected (the stored hash is never overwritten), so there is nothing
 * to gain by asking. `claimToken` would quietly mint in that case and hide the
 * distinction.
 */
export function peekClaimToken(campaign: string, userId: string): string | null {
  try {
    return localStorage.getItem(claimTokenKey(campaign, userId))
  } catch {
    return null
  }
}

/** Test/reset helper. Never called on a `try_again`: that keeps the token. */
export function clearClaimToken(campaign: string, userId: string): void {
  try {
    localStorage.removeItem(claimTokenKey(campaign, userId))
  } catch {
    // ignore
  }
}

// --- RPCs

// --- the current launch campaign
//
// The campaign used to be one hardcoded constant ('fra-2026-launch'), which
// meant every new set needed a client release before its popup could fire.
// The server now names it: `watchtower_current_campaign()` returns
// `{campaign_id, family_id, activated_at}`, with campaign_id null when no
// launch is live. Every set's campaign is `<family_id>-launch`.
//
// There is deliberately NO fallback campaign. Anything short of a well-formed
// id - an error, the function not deployed yet, a malformed shape, an id that
// does not look like a launch campaign - reads as "no campaign", and no
// campaign means no popup and no shown mark. Failing closed costs one load's
// popup; failing open to a stale campaign would mark the wrong one shown.

const CAMPAIGN_ID = /^[a-z0-9][a-z0-9_-]{0,63}-launch$/

/**
 * Read the RPC's jsonb object down to a campaign id, or null.
 *
 * Exported for tests. Exactly one object with a string campaign_id that
 * matches the launch-campaign pattern; everything else is null.
 */
export function normalizeCurrentCampaign(data: unknown): string | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null
  const id = (data as { campaign_id?: unknown }).campaign_id
  return typeof id === 'string' && CAMPAIGN_ID.test(id) ? id : null
}

// One promise per signed-in user for the page session. Keyed by user so an
// account switch asks again and can never read the previous user's answer.
const currentCampaignByUser = new Map<string, Promise<string | null>>()
// The not-deployed-yet case is expected for a while after this ships. Say so
// once, at debug level, rather than on every resolve.
let loggedMissingCampaignRpc = false

/**
 * The launch campaign the popup should ask about, or null for none.
 *
 * Single-flight AND cached per user for the page session: the popup, its
 * StrictMode replay and a remount all share one RPC. An answer (including
 * "none") and a missing function are cached; a transient error is not, so a
 * later mount for the same user may ask again. Never throws.
 */
export function currentCampaign(userId: string): Promise<string | null> {
  if (!userId) return Promise.resolve(null)
  const cached = currentCampaignByUser.get(userId)
  if (cached) return cached
  const call: Promise<string | null> = currentCampaignOnce().then(
    result => {
      if (result.retry && currentCampaignByUser.get(userId) === call) currentCampaignByUser.delete(userId)
      return result.campaign
    },
    () => {
      if (currentCampaignByUser.get(userId) === call) currentCampaignByUser.delete(userId)
      return null
    },
  )
  currentCampaignByUser.set(userId, call)
  return call
}

/** Test seam. A real page load starts with an empty cache anyway. */
export function resetCurrentCampaignCache(): void {
  currentCampaignByUser.clear()
  loggedMissingCampaignRpc = false
}

async function currentCampaignOnce(): Promise<{ campaign: string | null; retry: boolean }> {
  if (!supabase) return { campaign: null, retry: false }
  const { data, error } = await supabase.rpc('watchtower_current_campaign')
  if (error) {
    if (missingFunction(error)) {
      if (!loggedMissingCampaignRpc) {
        loggedMissingCampaignRpc = true
        console.debug('[watchtower] watchtower_current_campaign is not deployed; no launch popup')
      }
      return { campaign: null, retry: false }
    }
    return { campaign: null, retry: true }
  }
  return { campaign: normalizeCurrentCampaign(data), retry: false }
}


export type ClaimResult =
  /**
   * The claim is this person's. `reportId` is the report they can see on the
   * ACTIVE run right now, or null when they have none on it - the claim never
   * stores a report id, so this is re-resolved on every call and a retry after
   * a supersede answers with the newer run's report.
   */
  | { status: 'claimed'; reportId: string | null; runId: string | null; deckId: string | null; claimedAt: string | null }
  /** nothing has published yet; keep the token, ask again on the NEXT open */
  | { status: 'not_yet' }
  /** a publish held the generation row; keep the token, ask again on the NEXT open */
  | { status: 'try_again' }
  /** a DIFFERENT token already owns this (campaign, user). This device can never win it */
  | { status: 'token_mismatch' }
  /** WT011: the token we sent is not a UUID. Throw it away and mint a new one */
  | { status: 'bad_token' }
  /** the RPC is not deployed yet */
  | { status: 'unavailable' }
  | { status: 'error'; message: string }

/**
 * The RPC's own error codes, which arrive in the PostgREST error BODY.
 *
 * PostgREST only derives an HTTP status from SQLSTATEs it recognizes, so a
 * custom `WT0xx` comes back as a plain 500 carrying `{"code": "WT013"}`. Same
 * rule as deck_busy: key on the body, never the status.
 */
function errorCode(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null
  const code = (error as { code?: unknown }).code
  return typeof code === 'string' ? code : null
}

/**
 * Read `watchtower_claim_launch`'s single jsonb object.
 *
 * Narrow on purpose (the function is deployed and its shape is frozen in
 * migrations/2026_09_03_watchtower_05_claims.sql): exactly one object, exactly
 * the four status words. Anything else is a contract break, and the safe
 * reading of a contract break is `error` - which shows nothing, marks nothing
 * and asks again next time.
 */
export function normalizeClaim(data: unknown): ClaimResult {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return { status: 'error', message: 'Unrecognized claim response' }
  }
  const row = data as {
    status?: unknown
    report_id?: unknown
    run_id?: unknown
    deck_id?: unknown
    claimed_at?: unknown
  }
  switch (row.status) {
    case 'claimed':
      return {
        status: 'claimed',
        reportId: typeof row.report_id === 'string' ? row.report_id : null,
        runId: typeof row.run_id === 'string' ? row.run_id : null,
        deckId: typeof row.deck_id === 'string' ? row.deck_id : null,
        claimedAt: typeof row.claimed_at === 'string' ? row.claimed_at : null,
      }
    case 'not_yet':
      return { status: 'not_yet' }
    case 'try_again':
      return { status: 'try_again' }
    case 'token_mismatch':
      return { status: 'token_mismatch' }
    default:
      return { status: 'error', message: 'Unrecognized claim status' }
  }
}

/**
 * Claim the launch campaign for the signed-in user.
 *
 * ONE call, never a loop. `not_yet` and `try_again` are RESULTS rather than
 * errors by design: nothing has published, or a publish holds the generation
 * row, and either way the answer would only be right after it commits. The
 * token is kept and the next open asks again. Looping here would spin against a
 * transaction whose whole job is to take its time.
 */
export async function claimLaunch(campaign: string, token: string): Promise<ClaimResult> {
  // Single-flight per (campaign, token). Two callers can want the same claim
  // in the same instant - the root launch popup and a `/watchtower?campaign=`
  // page both resolve it on load - and the server takes a row lock per
  // (campaign, user), so two concurrent calls contend with each other: the
  // loser can come back `try_again` and the deep link then lands on the list
  // instead of the report it was sent for. The second caller shares the first
  // call's promise instead. Only IN-FLIGHT calls are shared; a call after the
  // first settles is a fresh RPC, because the claim re-resolves the current
  // report every time and a later call is a later question.
  const key = `${campaign}:${token}`
  const inFlight = claimsInFlight.get(key)
  if (inFlight) return inFlight
  const call = claimLaunchOnce(campaign, token).finally(() => {
    if (claimsInFlight.get(key) === call) claimsInFlight.delete(key)
  })
  claimsInFlight.set(key, call)
  return call
}

const claimsInFlight = new Map<string, Promise<ClaimResult>>()

async function claimLaunchOnce(campaign: string, token: string): Promise<ClaimResult> {
  if (!supabase) return { status: 'unavailable' }
  const { data, error } = await supabase.rpc('watchtower_claim_launch', {
    p_campaign: campaign,
    p_token: token,
  })
  if (error) {
    if (missingFunction(error)) return { status: 'unavailable' }
    // WT011 is the one error worth reacting to: the persisted token is not a
    // UUID, so the caller should discard it and mint a fresh one. WT010
    // (session expired mid-call), WT012 (campaign built wrong) and WT013 are
    // all "do nothing this session".
    if (errorCode(error) === 'WT011') return { status: 'bad_token' }
    return { status: 'error', message: error.message }
  }
  return normalizeClaim(data)
}

export type ClaimStatus =
  | { status: 'claimed'; claimedAt: string }
  | { status: 'unclaimed' }
  | { status: 'unavailable' }
  | { status: 'error'; message: string }

/** The caller's `claimed_at` for this campaign, or unclaimed. Takes no lock. */
export async function claimStatus(campaign: string): Promise<ClaimStatus> {
  if (!supabase) return { status: 'unavailable' }
  const { data, error } = await supabase.rpc('watchtower_claim_status', { p_campaign: campaign })
  if (error) {
    if (missingFunction(error)) return { status: 'unavailable' }
    return { status: 'error', message: error.message }
  }
  const at = readClaimedAt(data)
  return at ? { status: 'claimed', claimedAt: at } : { status: 'unclaimed' }
}

function readClaimedAt(data: unknown): string | null {
  if (typeof data === 'string') return data
  if (Array.isArray(data)) return data.length === 0 ? null : readClaimedAt(data[0])
  if (data && typeof data === 'object') {
    const at = (data as { claimed_at?: unknown }).claimed_at
    return typeof at === 'string' ? at : null
  }
  return null
}

export type MarkSeenResult = 'ok' | 'unavailable' | 'error'

/**
 * Idempotent read receipt for the caller's own published, non-superseded
 * report. Fire and forget from the page: failing to record that someone opened
 * a report must never stop them reading it.
 *
 * A report that is not the caller's, or not on the active run, RAISES WT013
 * server-side; that arrives here as an ordinary error and is swallowed, which
 * is the right outcome for a receipt.
 */
export async function markSeen(reportId: string): Promise<MarkSeenResult> {
  if (!supabase || !reportId) return 'unavailable'
  const { error } = await supabase.rpc('watchtower_mark_seen', { p_report_id: reportId })
  if (error) return missingFunction(error) ? 'unavailable' : 'error'
  return 'ok'
}
